import { type RefObject, useEffect, useRef } from 'react';

import { UNSAVED_CHANGES_MESSAGE } from './form-constants';

interface UseFormUnsavedChangesGuardArgs {
  isDirty: boolean;
  hasExternalUnsavedChanges: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  guardUnsavedChanges: boolean;
  blockRouterNavigation: boolean;
}

interface UseFormUnsavedChangesGuardResult {
  hasUnsavedChanges: boolean;
  hasUnsavedChangesRef: RefObject<boolean>;
}

export function useFormUnsavedChangesGuard({
  isDirty,
  hasExternalUnsavedChanges,
  onDirtyChange,
  guardUnsavedChanges,
  blockRouterNavigation,
}: UseFormUnsavedChangesGuardArgs): UseFormUnsavedChangesGuardResult {
  const hasUnsavedChanges = isDirty || hasExternalUnsavedChanges;
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!(guardUnsavedChanges && hasUnsavedChanges)) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChangesRef.current) return;
      event.preventDefault();
    };
    const handleDocumentClick = (event: MouseEvent) => {
      if (!hasUnsavedChangesRef.current) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest('a[href]');
      if (!(link && link instanceof HTMLAnchorElement) || link.href === window.location.href) return;
      if (!window.confirm(UNSAVED_CHANGES_MESSAGE)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    if (!blockRouterNavigation) document.addEventListener('click', handleDocumentClick, true);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (!blockRouterNavigation) document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [blockRouterNavigation, guardUnsavedChanges, hasUnsavedChanges]);

  return { hasUnsavedChanges, hasUnsavedChangesRef };
}
